import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { MapPin, Shield, X, AlertTriangle, Check } from "lucide-react";

const CONSENT_KEY = "mytacs_geo_consent_v1";

export function getGeoConsent() {
    try {
        const raw = localStorage.getItem(CONSENT_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch { return null; }
}
export function setGeoConsent(v) {
    try {
        const payload = v ? {
            accepted: true,
            accepted_at: new Date().toISOString(),
            version: 1,
            user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 200) : "",
        } : { accepted: false, declined_at: new Date().toISOString(), version: 1 };
        localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
    } catch { /* storage blocked */ }
}

/**
 * One-time GDPR-compliant consent dialog for GPS access at clock-in/clock-out.
 * Persists the decision per device in localStorage. If the user accepts, the
 * decision is remembered forever on this device; if declined, the user is
 * blocked from clocking in/out until they accept.
 */
export default function GeoConsentDialog({ open, onAccept, onDecline, lang = "de" }) {
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onDecline?.(); }}>
            <DialogContent className="max-w-lg" data-testid="geo-consent-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display text-2xl">
                        <MapPin className="w-6 h-6 text-brand-600" />
                        {lang === "de" ? "Standort-Zugriff für die Stechuhr" : "Location access for time-clock"}
                    </DialogTitle>
                    <DialogDescription>
                        {lang === "de"
                            ? "Bitte einmalige Bestätigung für dieses Gerät."
                            : "One-time confirmation per device."}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 text-sm text-neutral-700 max-h-[55vh] overflow-y-auto pr-1">
                    <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 flex gap-2">
                        <Shield className="w-4 h-4 text-emerald-700 shrink-0 mt-0.5" />
                        <p>
                            {lang === "de"
                                ? "Wir orten dein Gerät NICHT dauerhaft. Der Standort wird ausschließlich im Moment des Ein- und Ausstempelns ermittelt — und sofort wieder verworfen."
                                : "We do NOT track your device continuously. Location is only read at the exact moment you clock in or out, then discarded."}
                        </p>
                    </div>

                    <p className="font-semibold text-neutral-800">{lang === "de" ? "Was wir tun:" : "What we do:"}</p>
                    <ul className="list-disc pl-5 space-y-1 text-[13px]">
                        <li>{lang === "de" ? "Standort wird nur abgerufen, wenn du auf „Einstempeln“ oder „Ausstempeln“ klickst." : "Location is read only when you click Clock-in/Clock-out."}</li>
                        <li>{lang === "de" ? "Genauigkeit dient ausschließlich dem Abgleich mit deinem zugewiesenen Arbeitsort (Geofence) — Pflicht laut Arbeitszeitgesetz (ArbZG) zur korrekten Zeiterfassung." : "Used solely to verify your assigned worksite (geofence) — required by working-time law for accurate timekeeping."}</li>
                        <li>{lang === "de" ? "Datenminimierung gemäß DSGVO Art. 5: keine permanente Bewegungsanalyse, keine Hintergrund-Ortung." : "GDPR Art. 5 minimisation: no movement profile, no background tracking."}</li>
                        <li>{lang === "de" ? "Speicherdauer: Koordinaten werden mit dem Zeiteintrag verknüpft und gemäß steuerlicher Aufbewahrungsfrist gespeichert (bis 10 Jahre)." : "Storage: coordinates linked to the time entry, kept according to fiscal record-keeping (up to 10 years)."}</li>
                        <li>{lang === "de" ? "Du kannst die Einwilligung jederzeit in den Browser-Einstellungen widerrufen." : "You can revoke consent any time in your browser settings."}</li>
                    </ul>

                    <p className="font-semibold text-neutral-800 mt-2">{lang === "de" ? "Rechtsgrundlage:" : "Legal basis:"}</p>
                    <ul className="list-disc pl-5 space-y-1 text-[13px]">
                        <li>{lang === "de" ? "DSGVO Art. 6 Abs. 1 lit. b (Vertragserfüllung — Arbeitsvertrag) und Art. 6 Abs. 1 lit. f (berechtigtes Interesse zur korrekten Zeiterfassung)." : "GDPR Art. 6(1)(b) (contract performance) and 6(1)(f) (legitimate interest)."}</li>
                        <li>{lang === "de" ? "§ 16 ArbZG (Aushang) und EuGH-Urteil C-55/18 (verpflichtende Arbeitszeitmessung)." : "Working Hours Act § 16 and ECJ C-55/18 (mandatory time recording)."}</li>
                        <li>{lang === "de" ? "Diese Einwilligung ist gerätegebunden und gilt unbefristet, bis du sie widerrufst." : "Consent is device-bound and indefinite until you revoke it."}</li>
                    </ul>

                    <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 flex gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                        <p>
                            {lang === "de"
                                ? "Wenn du nicht zustimmst, kannst du auf diesem Gerät weder ein- noch ausstempeln. Du kannst stattdessen den Stechvorgang im Büro durchführen oder das Admin-Team um eine manuelle Erfassung bitten."
                                : "If you decline, you can't clock in/out on this device. Use a workplace device or ask admin for manual entry."}
                        </p>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 pt-3 border-t border-neutral-200">
                    <Button variant="outline" onClick={onDecline} className="flex-1 h-11" data-testid="geo-consent-decline">
                        <X className="w-4 h-4 mr-1" /> {lang === "de" ? "Ablehnen" : "Decline"}
                    </Button>
                    <Button onClick={onAccept} className="flex-1 h-11 bg-brand-600 hover:bg-brand-700 text-white" data-testid="geo-consent-accept">
                        <Check className="w-4 h-4 mr-1" /> {lang === "de" ? "Einwilligung erteilen" : "I consent"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/** Wraps a clock-in/out action behind a consent gate. */
export function useGeoConsent() {
    const [consent, setConsent] = useState(() => getGeoConsent());
    const [askOpen, setAskOpen] = useState(false);
    const ensure = () => new Promise((resolve) => {
        const c = getGeoConsent();
        if (c?.accepted) return resolve(true);
        // Trigger dialog and wait
        setAskOpen(true);
        ensure._resolve = resolve;
    });
    useEffect(() => () => { ensure._resolve = null; }, []);
    const accept = () => {
        setGeoConsent(true);
        setConsent(getGeoConsent());
        setAskOpen(false);
        if (ensure._resolve) { ensure._resolve(true); ensure._resolve = null; }
    };
    const decline = () => {
        setGeoConsent(false);
        setConsent(getGeoConsent());
        setAskOpen(false);
        if (ensure._resolve) { ensure._resolve(false); ensure._resolve = null; }
    };
    return { consent, askOpen, ensure, accept, decline, setAskOpen };
}
